"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Icon } from "@/components/ui/icon";
import { deliverables } from "@/lib/data/homepage";

/**
 * Deliverables — Document/asset mockup grid. Each deliverable is shown
 * as a stylized "document" with icon, title, body, and a "preview" header.
 */
export function Deliverables() {
  const accents = [
    "var(--color-crimson)",
    "var(--color-purple)",
    "var(--color-cyan)",
    "var(--color-emerald)",
    "var(--color-amber)",
  ];
  return (
    <section id="deliverables" className="relative section-pad">
      <Container>
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-purple)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-purple)]" />
            Deliverables
          </span>
          <h2 className="mt-6">
            What You Receive
          </h2>
          <p className="mt-4 text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl mx-auto">
            Tangible, measurable artifacts from every engagement.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {deliverables.map((d, i) => (
            <Reveal key={d.title} delay={i * 60}>
              <div
                className="rounded-2xl glass p-5 h-full relative overflow-hidden card-glow group"
                style={{ ["--glow" as string]: accents[i % accents.length] }}
              >
                {/* Document header mockup */}
                <div className="flex items-center gap-1.5 mb-3">
                  <div className="h-2 w-2 rounded-full" style={{ background: accents[i % accents.length] }} />
                  <div className="h-2 w-2 rounded-full bg-[rgb(var(--border)/0.3)]" />
                  <div className="h-2 w-2 rounded-full bg-[rgb(var(--border)/0.3)]" />
                </div>
                {/* Faux content lines */}
                <div className="space-y-1.5 mb-4">
                  <div className="h-1.5 rounded-full bg-[rgb(var(--border)/0.15)]" style={{ width: "100%" }} />
                  <div className="h-1.5 rounded-full bg-[rgb(var(--border)/0.15)]" style={{ width: "80%" }} />
                  <div className="h-1.5 rounded-full bg-[rgb(var(--border)/0.15)]" style={{ width: "60%" }} />
                </div>
                {/* Icon + title */}
                <div
                  className="grid place-items-center h-10 w-10 rounded-lg mb-3 transition-transform group-hover:scale-110"
                  style={{
                    background: `color-mix(in oklch, ${accents[i % accents.length]} 18%, transparent)`,
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accents[i % accents.length]} 35%, transparent)`,
                  }}
                >
                  <Icon name={d.icon} className="h-5 w-5" style={{ color: accents[i % accents.length] }} />
                </div>
                <h3 className="mb-1.5">{d.title}</h3>
                <p className="text-caption leading-relaxed text-[rgb(var(--fg-muted))]">{d.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
